import { HTMLAttributes, useEffect, useMemo, useRef } from 'react'
import { Transition } from 'react-transition-group'
import gsap from 'gsap'

import SVGIcon from '@/components/base/svg-icon'
import { useLoaderStore } from '@/store/loader'
import { pageHasHero } from '@/utils/page'
import { isAcademy, isCorporate } from '@/constants'

import Spinner from './spinner'
import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type LoaderProps = HTMLAttributes<HTMLDivElement> & {
  pageProps: WPPageData
}

const Loader = ({ pageProps, ...props }: LoaderProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])
  const heroLoaded = useLoaderStore((state) => state.heroLoaded)
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)

  useEffect(() => {
    const call = gsap.delayedCall(hasHero ? 3 : 0.3, () => {
      setLoaderState({ heroLoaded: true, heroCoverLoaded: true })
    })

    return () => {
      call.kill()
    }
  }, [hasHero, setLoaderState])

  return (
    <Transition
      nodeRef={root}
      in={!heroLoaded}
      timeout={{
        enter: 0,
        exit: 1000
      }}
      unmountOnExit
    >
      {(state) => (
        <div
          ref={root}
          className={cn(
            'fixed left-0 right-0 top-0 z-loader h-full w-full',
            'bg-section-backgroud-light dark:bg-section-backgroud-dark',
            'flex items-center justify-center',
            'transition-opacity delay-500 duration-500 ease-in-out-cubic',
            {
              'opacity-0 duration-0': state === 'entering',
              'opacity-100': state === 'entered',
              'opacity-0': state === 'exiting'
            }
          )}
          {...props}
        >
          {isCorporate ? (
            <SVGIcon
              symbol="logo-corporate-condensed"
              className="h-[2.8rem] w-[8.5rem] fill-surface-logo-light dark:fill-surface-logo-dark"
            />
          ) : null}

          {isAcademy ? (
            <SVGIcon
              symbol="logo-academy-condensed"
              className="h-[3.4rem] w-[12rem]"
            />
          ) : null}

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <Spinner variant="large" />
          </div>
        </div>
      )}
    </Transition>
  )
}

export default Loader
